import { RouteItem } from 'types/route.types';
import { RouteItemId } from 'common/enums/html.enums';
import { Routes } from 'constants/route.constants';
import { ReactComponent as UsersIcon } from 'img/icons/users.svg';
import { ReactComponent as CorporationsIcon } from 'img/icons/sidebar/building.svg';
import { ReactComponent as SettingsIcon } from 'img/icons/sidebar/Gear.svg';
import { ReactComponent as DictionaryIcon } from 'img/icons/sidebar/book.svg';
import { ReactComponent as KeyIcon } from 'img/icons/key.svg';
import { ReactComponent as SendMoneyIcon } from 'img/icons/sidebar/send-money.svg';
import { ReactComponent as UserSettingsIcon } from 'img/icons/user-settings.svg';
import { ReactComponent as LockClosedIcon } from 'img/icons/lock-closed.svg';
import { ReactComponent as CalculatorIcon } from 'img/icons/sidebar/calculator.svg';
import { ReactComponent as SpambiIcon } from 'img/icons/sidebar/spambi.svg';
import { ReactComponent as UndoIcon } from 'img/icons/undo-solid.svg';
import { ReactComponent as DocumentChartIcon } from 'img/icons/document-chart.svg';
import { ReactComponent as ClockRotateIcon } from 'img/icons/clock-rotate.svg';
import { PoliciesPermissionSetItems as Permission } from 'common/constants/permissions';
import { IRouteItemContainer, IRouteItemNavLink } from 'interfaces/route.interfaces';
import inputLabels from 'common/messages/inputLabels';
import corporationMessages from 'modules/corporations/messages';
import menuItemMessages from 'common/messages/menuItems';
import permissionMessages from 'modules/permissions/messages/messages';
import commonMessages from 'common/messages/messages';
import remittanceMessages from 'modules/remittance/messages';
import reportMessages from 'modules/reports/messages/messages';
import { homeNavLink } from './routeItemNavLink.constants';

const peakDictionaryNavLink: IRouteItemNavLink = {
  type: 'navLink',
  id: RouteItemId.RouteItemBtnDictionariesContainer,
  path: Routes.DICTIONARIES,
  title: commonMessages.dictionaries,
  icon: DictionaryIcon,
  iconViewBox: '0 0 20 20',
  permissions: [Permission.PEAK_HELP_INFORMATION_VIEW, Permission.PEAK_ADJUSTMENT_CATEGORY_VIEW],
};

export const peakPermissionsContainer: IRouteItemContainer = {
  type: 'container',
  id: RouteItemId.RouteItemBtnPermissionsContainer,
  path: Routes.PERMISSIONS,
  title: commonMessages.permissions,
  icon: KeyIcon,
  iconViewBox: '0 0 24 24',
  children: [
    {
      type: 'navLink',
      id: RouteItemId.RouteItemBtnRoles,
      path: Routes.ROLES,
      title: permissionMessages.roles,
      icon: UserSettingsIcon,
      iconViewBox: '0 0 40 40',
      permissions: [Permission.PEAK_SECURITY_ROLE_VIEW],
    },
    {
      type: 'navLink',
      id: RouteItemId.RouteItemBtnPolicies,
      path: Routes.POLICIES,
      title: permissionMessages.policies,
      icon: LockClosedIcon,
      iconViewBox: '0 0 16 16',
      permissions: [Permission.PEAK_SECURITY_POLICY_VIEW],
    },
  ],
};

const peakEmployeesNavLink: IRouteItemNavLink = {
  type: 'navLink',
  id: RouteItemId.RouteItemBtnPeakEmployees,
  path: Routes.PEAK_EMPLOYEES,
  title: inputLabels.employeesLabel,
  icon: UsersIcon,
  iconViewBox: '0 0 20 20',
  permissions: [Permission.PEAK_EMPLOYEE_VIEW],
};

const peakCorporationsNavLink: IRouteItemNavLink = {
  type: 'navLink',
  id: RouteItemId.RouteItemBtnCorporations,
  path: Routes.CORPORATIONS,
  title: corporationMessages.corporationsTitle,
  icon: CorporationsIcon,
  iconViewBox: '0 0 20 20',
  permissions: [
    Permission.PEAK_ORGANIZATION_GENERAL_INFO_VIEW,
    Permission.PEAK_ORGANIZATION_BANK_INFO_VIEW,
    Permission.PEAK_ORGANIZATION_BILLING_INFO_VIEW,
    Permission.PEAK_ORGANIZATION_FEE_RATES_VIEW,
    Permission.PEAK_ORGANIZATION_PROCESSOR_CONFIGURATION_VIEW,
    Permission.PEAK_ORGANIZATION_TAXES_INFO_VIEW,
    Permission.PEAK_ORGANIZATION_INTEGRATION_VIEW,
    Permission.PEAK_ORGANIZATION_OTHER_VIEW,
    Permission.PEAK_ORGANIZATION_REQUIRED_FIELDS_VIEW,
    Permission.PEAK_ORGANIZATION_PAY_PERIOD_SETTINGS_VIEW,
  ],
};

const peakSettingsNavLink: IRouteItemNavLink = {
  type: 'navLink',
  id: RouteItemId.RouteItemBtnSettings,
  path: Routes.PEAK_SETTINGS,
  title: menuItemMessages.settings,
  icon: SettingsIcon,
  iconViewBox: '0 0 20 20',
  permissions: [Permission.PEAK_SETTINGS_INTEGRATION_VIEW, Permission.PEAK_SETTINGS_GENERAL_VIEW],
};

export const peakRemittanceContainer: IRouteItemContainer = {
  type: 'container',
  id: RouteItemId.RouteItemBtnRemittanceContainer,
  path: Routes.PEAK_REMITTANCE,
  title: remittanceMessages.remittance,
  icon: SendMoneyIcon,
  iconViewBox: '0 0 20 16',
  children: [
    {
      type: 'navLink',
      id: RouteItemId.RouteItemBtnRemittanceCalculate,
      path: Routes.PEAK_REMITTANCE_CALCULATE,
      title: remittanceMessages.calculateRemittance,
      icon: SendMoneyIcon,
      iconViewBox: '0 0 20 16',
      permissions: [Permission.PEAK_REMITTANCE_VIEW],
    },
  ],
};

const peakReconciliationNavLink: IRouteItemNavLink = {
  type: 'navLink',
  id: RouteItemId.RouteItemBtnReconciliation,
  path: Routes.DEPOSIT_RECONCILIATION,
  title: commonMessages.depositReconciliation,
  icon: CalculatorIcon,
  iconViewBox: '0 0 12 18',
  permissions: [Permission.PEAK_REMITTANCE_VIEW],
};

export const peakAdjustmentsContainer: IRouteItemContainer = {
  type: 'container',
  id: RouteItemId.RouteItemBtnPeakClientAdjustmentsContainer,
  path: Routes.PEAK_CLIENT_ADJUSTMENTS,
  title: commonMessages.clientAdjustments,
  icon: SpambiIcon,
  iconViewBox: '0 0 14 18',
  children: [
    {
      type: 'navLink',
      id: RouteItemId.RouteItemBtnPeakClientAdjustmentsClientAdjustmentsContainer,
      path: Routes.PEAK_CLIENT_ADJUSTMENTS_LIST,
      title: commonMessages.clientAdjustmentsList,
      icon: SpambiIcon,
      iconViewBox: '0 0 14 18',
      permissions: [Permission.PEAK_CLIENT_ADJUSTMENTS_VIEW],
    },
    {
      type: 'navLink',
      id: RouteItemId.RouteItemBtnPeakRecurringClientAdjustmentsContainer,
      path: Routes.PEAK_RECURRING_CLIENT_ADJUSTMENTS,
      title: commonMessages.recurringAdjustments,
      icon: UndoIcon,
      iconViewBox: '0 0 24 24',
      permissions: [Permission.PEAK_RECURRING_CLIENT_ADJUSTMENTS_VIEW],
    },
  ],
};

export const peakReportsContainer: IRouteItemContainer = {
  type: 'container',
  id: RouteItemId.RouteItemBtnPeakReportsContainer,
  path: Routes.PEAK_REPORTS,
  title: commonMessages.reports,
  icon: DocumentChartIcon,
  iconViewBox: '0 0 20 20',
  children: [
    {
      type: 'navLink',
      id: RouteItemId.RouteItemBtnPeakReportPaymentTransactions,
      path: Routes.PEAK_REPORT_PAYMENT_TRANSACTIONS,
      title: reportMessages.paymentTransactionsReport,
      icon: ClockRotateIcon,
      iconViewBox: '0 0 24 24',
      permissions: [Permission.PEAK_CLIENT_ADJUSTMENTS_VIEW],
    },
  ],
};

export const peakRouteItems: RouteItem[] = [
  homeNavLink,
  peakDictionaryNavLink,
  peakPermissionsContainer,
  peakEmployeesNavLink,
  peakCorporationsNavLink,
  peakSettingsNavLink,
  peakRemittanceContainer,
  peakReconciliationNavLink,
  peakReportsContainer,
  peakAdjustmentsContainer,
];
